import React from "react";
import { useToast } from "@chakra-ui/react";
import { StepperModal } from "../../../_components/Modals";
import { CompanyInformationStep } from "./steps/1-company-information";
import { ContactDetailsStep } from "./steps/2-contact-details";
import { CommunityInformationStep } from "./steps/3-community-information";
import { SalesConversationPreferenceStep } from "./steps/4-sales-conversation-preference";
import {
  CommunitySalesConversationProvider,
  useCommunitySalesConversation,
} from "../context/community-sales-conversation.context";
import {
  CompanyInformationFormData,
  ContactDetailsFormData,
  CommunityInformationFormData,
  SalesConversationPreferenceFormData,
} from "../schemas/schema";
import { logEvent } from "../../../helpers/analytics";

interface CommunitySalesConversationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CommunitySalesConversationModalContent: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const {
    currentStep,
    nextStep,
    previousStep,
    submitForm,
    resetForm,
    updateCompanyInfo,
    updateContactDetails,
    updateCommunityInfo,
    updateSalesConversationPreference,
    getAllFormData,
    getCompanyInfo,
    getContactDetails,
    getCommunityInfo,
    getSalesConversationPreference,
    isLoading,
  } = useCommunitySalesConversation();

  const toast = useToast();

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "Tell us about your organization";
      case 2:
        return "Contact details";
      case 3:
        return "Community information";
      case 4:
        return "Book a call";
      default:
        return "Talk to Sales";
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCompanyInfoComplete = (data: CompanyInformationFormData) => {
    updateCompanyInfo(data);
    nextStep();
  };

  const handleContactDetailsComplete = (data: ContactDetailsFormData) => {
    updateContactDetails(data);
    nextStep();
  };

  const handleCommunityInfoComplete = (data: CommunityInformationFormData) => {
    updateCommunityInfo(data);
    nextStep();
  };

  const handleSalesConversationPreferenceComplete = async (data: SalesConversationPreferenceFormData) => {
    updateSalesConversationPreference(data);

    // Get all form data and prepare for submission
    const allFormData = { ...getAllFormData(), ...data };

    // Submit using the mutation
    await submitForm({
      variables: {
        communityManagementSalesConversationRequestDto: {
          organization: allFormData.organization!,
          roleInOrganization: allFormData.roleInOrganization!,
          firstName: allFormData.firstName!,
          lastName: allFormData.lastName!,
          email: allFormData.email!,
          phone: allFormData.phone!,
          typeOfCommunity: allFormData.typeOfCommunity!,
          communitySize: allFormData.communitySize!,
          communityTalent: allFormData.communityTalent!,
          majorChallenges: allFormData.majorChallenges!,
          digitalToolsUsed: allFormData.digitalToolsUsed,
          preferredSalesConversationDate: allFormData.preferredSalesConversationDate.toISOString(),
          preferredSalesConversationTime: allFormData.preferredSalesConversationTime,
        },
      },
      onCompleted: () => {
        toast({
          title: "Request Submitted Successfully",
          description: "We'll contact you soon for a conversation and next steps.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        handleClose();
        logEvent("community_sales_conversation_submit_success");
      },
      onError: (error) => {
        toast({
          title: "Submission Failed",
          description:
            "Please try again or contact support if the issue persists.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        logEvent("community_sales_conversation_error", { error: error.message });
      },
    });
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <CompanyInformationStep
            onStepComplete={handleCompanyInfoComplete}
            isLoading={isLoading}
            defaultValues={getCompanyInfo()}
          />
        );
      case 2:
        return (
          <ContactDetailsStep
            onStepComplete={handleContactDetailsComplete}
            onPrevious={previousStep}
            isLoading={isLoading}
            defaultValues={getContactDetails()}
          />
        );
      case 3:
        return (
          <CommunityInformationStep
            onStepComplete={handleCommunityInfoComplete}
            onPrevious={previousStep}
            isLoading={isLoading}
            defaultValues={getCommunityInfo()}
          />
        );
      case 4:
        return (
          <SalesConversationPreferenceStep
            onStepComplete={handleSalesConversationPreferenceComplete}
            onPrevious={previousStep}
            isLoading={isLoading}
            defaultValues={getSalesConversationPreference()}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StepperModal
      isOpen={true}
      onClose={handleClose}
      title={getStepTitle()}
      onSubmit={() => {}}
      currentStep={currentStep}
      totalSteps={4}
      isLoading={isLoading}
      onReset={resetForm}
    >
      {renderCurrentStep()}
    </StepperModal>
  );
};

export const CommunitySalesConversationModal: React.FC<CommunitySalesConversationModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <CommunitySalesConversationProvider>
      <CommunitySalesConversationModalContent onClose={onClose} />
    </CommunitySalesConversationProvider>
  );
};
